import React, { useEffect, useState } from "react";
import {
    Button,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Text,
    Textarea,
    Checkbox,
    HStack
} from "@chakra-ui/react";

/**
 * Renders the ban-specific fields: mandatory reason and the (automatic)
 * notification indicator. Shown only when banning, not when unbanning.
 * @param {Object} props
 * @param {string} props.reason - Current reason text
 * @param {Function} props.setReason - Setter for the reason field
 * @returns {React.ReactElement}
 */
function BanReasonFields({ reason, setReason }) {
    return (
        <>
            <Textarea
                rows={3} placeholder="Motif (obligatoire)" value={reason}
                onChange={(e) => setReason(e.target.value)} mt={2} {...banFieldSx}
            />
            <HStack mt={2} spacing={2} color="rgba(255,255,255,0.8)">
                <Checkbox isChecked isDisabled colorScheme="teal" iconColor="#17dcb8" />
                <Text fontSize="0.85rem">Notifier l'utilisateur (email + push, automatique)</Text>
            </HStack>
            <Text color="rgba(255,255,255,0.5)" fontSize="0.72rem" mt={1}>
                Le motif et la procédure de contestation (support@digiwide.io) sont inclus dans la notification.
            </Text>
        </>
    );
}

/**
 * Renders a confirmation dialog for banning or unbanning a user. Banning
 * requires a reason and notifies the user (email + push) via the server.
 * @param {Object} props
 * @param {boolean} props.showModal - Whether the dialog is open
 * @param {Function} props.setShowModal - State setter to close the dialog
 * @param {boolean} props.banned - Current ban status of the user
 * @param {Function} props.onConfirm - Handler called with the reason on confirm
 * @returns {React.ReactElement}
 */
function BanConfirmDialog({ showModal, setShowModal, banned, onConfirm }) {
    const [reason, setReason] = useState("");
    const isBanning = !banned;
    useEffect(() => { if (showModal) setReason(""); }, [showModal]);

    return (
        <Modal isOpen={showModal} onClose={() => setShowModal(false)} size="md" isCentered>
            <ModalOverlay />
            <ModalContent {...dialogPaperSx}>
                <ModalHeader color="rgba(255, 255, 255, 0.95)" fontWeight={600}>
                    Confirmation
                </ModalHeader>
                <ModalBody>
                    <Text color="rgba(255, 255, 255, 0.8)">
                        {isBanning ? "Confirmez-vous le bannissement de cet utilisateur ?" : "Confirmez-vous le débannissement de cet utilisateur ?"}
                    </Text>
                    {isBanning && <BanReasonFields reason={reason} setReason={setReason} />}
                </ModalBody>
                <ModalFooter gap={2}>
                    <CancelButton onClick={() => setShowModal(false)} />
                    <ConfirmButton onClick={() => onConfirm(reason.trim())} banned={banned} disabled={isBanning && !reason.trim()} />
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

/**
 * Renders the cancel button inside the ban dialog.
 * @param {Object} props
 * @param {Function} props.onClick - Click handler to close the dialog
 * @returns {React.ReactElement}
 */
function CancelButton({ onClick }) {
    return (
        <Button
            onClick={onClick}
            color="rgba(255, 255, 255, 0.8)"
            background="rgba(255, 255, 255, 0.05)"
            backdropFilter="blur(10px)"
            border="1px solid rgba(255, 255, 255, 0.1)"
            _hover={{
                background: "rgba(255, 255, 255, 0.1)",
                border: "1px solid rgba(255, 255, 255, 0.15)"
            }}
        >
            Annuler
        </Button>
    );
}

/**
 * Renders the confirm button inside the ban dialog, colored by ban status.
 * @param {Object} props
 * @param {Function} props.onClick - Click handler for confirm action
 * @param {boolean} props.banned - Whether the user is currently banned
 * @param {boolean} [props.disabled] - Whether the confirm action is disabled
 * @returns {React.ReactElement}
 */
function ConfirmButton({ onClick, banned, disabled }) {
    const colorBase = banned ? "76, 175, 80" : "244, 67, 54";

    return (
        <Button
            onClick={onClick}
            isDisabled={disabled}
            background={`rgba(${colorBase}, 0.15)`}
            backdropFilter="blur(10px)"
            color={`rgba(${colorBase}, 1)`}
            border={`1px solid rgba(${colorBase}, 0.4)`}
            fontWeight={600}
            _hover={{ background: `rgba(${colorBase}, 0.25)` }}
            _disabled={{ opacity: 0.5 }}
        >
            Confirmer
        </Button>
    );
}

/** Chakra style props for the dialog content container. */
const dialogPaperSx = {
    background: "rgba(30, 30, 30, 0.95)",
    backdropFilter: "blur(20px)",
    border: "1px solid rgba(255, 255, 255, 0.15)",
    borderRadius: "xl"
};

/** Chakra style props for the ban reason text field (dark theme). */
const banFieldSx = {
    color: "rgba(255, 255, 255, 0.9)",
    borderColor: "rgba(255, 255, 255, 0.2)",
    _placeholder: { color: "rgba(255, 255, 255, 0.5)" },
    _hover: { borderColor: "rgba(255, 255, 255, 0.3)" },
    _focus: { borderColor: "#17dcb8", boxShadow: "none" }
};

export default BanConfirmDialog;
